import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom'; // Import Link from React Router

const AboutUs = () => {
  return (
    <div className="bg-gray-50 min-h-screen py-16">
      <div className="container mx-auto px-6 md:px-12 bg-white rounded-lg shadow-lg">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-extrabold text-blue-600 mb-4">About Us</h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Welcome to{" "}
            <span className="font-semibold text-blue-600">Corehire Solutions</span>, your
            trusted partner in career consulting and job placement. We guide individuals
            toward professional success with personalized solutions.
          </p>
        </motion.div>

        {/* Who We Are Section */}
        <div className="flex flex-wrap items-center justify-between mt-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className="w-full md:w-1/2 p-4"
          >
            <h2 className="text-3xl font-semibold text-blue-500 mb-4">
              Who We Are
            </h2>
            <p className="text-lg text-gray-600">
              <span className="font-semibold text-blue-600">Corehire Solutions</span> is
              a team of dedicated professionals with expertise in career development,
              industry connections, and recruitment strategies. Our focus is on helping
              clients navigate the competitive job market with confidence.
            </p>
            <p className="text-lg text-gray-600 mt-4">
              With years of experience and a network spanning multiple industries, we
              bring opportunities and guidance tailored to individual needs.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2 }}
            className="w-full md:w-1/2 p-4"
          >
            <img
              src="https://img.freepik.com/free-photo/modern-equipped-computer-lab_23-2149241263.jpg"
              alt="Who We Are"
              className="rounded-lg shadow-md"
            />
          </motion.div>
        </div>

        {/* Mission and Vision Section */}
        <div className="flex flex-wrap items-center justify-between mt-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2 }}
            className="w-full md:w-1/2 p-4"
          >
            <img
              src="https://rsconsultancy.com/wp-content/uploads/2019/08/Vision-Mission.jpg"
              alt="Mission and Vision"
              className="rounded-lg shadow-md"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className="w-full md:w-1/2 p-4"
          >
            <h2 className="text-3xl font-semibold text-blue-500 mb-4">
              Our Mission and Vision
            </h2>
            <p className="text-lg text-gray-600">
              Our mission is to empower individuals to achieve their career
              aspirations through tailored guidance and industry insights.
            </p>
            <p className="text-lg text-gray-600 mt-4">
              We envision a world where job seekers are equipped with the tools and
              knowledge to reach their full potential, creating lasting impacts in their
              chosen fields.
            </p>
          </motion.div>
        </div>

        {/* Core Values Section */}
        <div className="text-center mt-16 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3 }}
          >
            <h2 className="text-3xl font-semibold text-blue-500 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We are committed to excellence, transparency, and personalized service.
              Our core values guide everything we do:
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4 }}
              className="p-6 bg-gray-100 rounded-lg shadow-md"
            >
              <img
                src="https://tmogllc.com/wp-content/uploads/2016/10/integrity-icon.png"
                alt="Integrity"
                className="mx-auto mb-4"
              />
              <h3 className="text-xl font-semibold text-blue-600 mb-2">
                Integrity
              </h3>
              <p className="text-gray-600">
                We uphold the highest standards of honesty and fairness in all our
                dealings.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5 }}
              className="p-6 bg-gray-100 rounded-lg shadow-md"
            >
              <img
                src="https://images.pexels.com/photos/1679618/pexels-photo-1679618.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                alt="Empowerment"
                className="mx-auto mb-4"
              />
              <h3 className="text-xl font-semibold text-blue-600 mb-2">
                Empowerment
              </h3>
              <p className="text-gray-600">
                We empower clients to take charge of their careers with
                confidence and knowledge.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.6 }}
              className="p-6 bg-gray-100 rounded-lg shadow-md"
            >
              <img
                src="https://symbioticconsultinggroup.com/ServicesImages/Collaboration.jpg"
                alt="Collaboration"
                className="mx-auto mb-4"
              />
              <h3 className="text-xl font-semibold text-blue-600 mb-2">
                Collaboration
              </h3>
              <p className="text-gray-600">
                We work closely with clients and partners to ensure shared success.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="text-center mt-16"
        >
         <Link to="/contact" className="block">
         <button className="px-8 py-3 bg-blue-600 text-white font-semibold text-xl rounded-lg shadow-md hover:bg-blue-700 transition-all duration-300 transform hover:scale-105">
            Contact Us
          </button>
          </Link>

          
        </motion.div>
      </div>
    </div>
  );
};

export default AboutUs;
